import React, { useEffect, useState } from 'react';
import { addItemByName, deleteItems, getCollection, queryItemsByText } from '../api/chromaApi';
import { useParams, Link as RouterLink } from 'react-router-dom';

export default function AdminChromaCollection() {
  const { name: routeName = '' } = useParams();

  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<any>(null);

  const [idsText, setIdsText] = useState('');
  const [itemName, setItemName] = useState('');
  const [document, setDocument] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [qText, setQText] = useState('');
  const [qResults, setQResults] = useState(2);
  const [queryResult, setQueryResult] = useState<any>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [tab, setTab] = useState<'info'|'add'|'delete'|'query'>('info');

  const [addPending, setAddPending] = useState(false);
  const [delPending, setDelPending] = useState(false);
  const [queryPending, setQueryPending] = useState(false);

  const fetchData = async () => {
    if (!routeName) return;
    setIsLoading(true);
    setIsError(false);
    setError(null);
    try {
      const res = await getCollection(routeName);
      setData(res);
    } catch (e: any) {
      setIsError(true);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeName]);

  const handleAdd = async () => {
    try {
      setOpError(null);
      setCreatedId(null);
      setAddPending(true);
      if (!itemName.trim()) throw new Error('Name is required');
      if (!data?.id) throw new Error('Collection ID is not loaded yet');
      const res = await addItemByName(data.id, { name: itemName, document: document || undefined });
      setItemName('');
      setDocument('');
      setCreatedId(res?.id || null);
      await fetchData();
    } catch (e: any) {
      setOpError(e?.message || 'Failed to add item');
    } finally {
      setAddPending(false);
    }
  };

  const handleDelete = async () => {
    try {
      setOpError(null);
      setDelPending(true);
      const ids = idsText.split('\n').map(s => s.trim()).filter(Boolean);
      if (!data?.id) throw new Error('Collection ID is not loaded yet');
      await deleteItems(data.id, ids);
      setIdsText('');
      await fetchData();
    } catch (e: any) {
      setOpError(e?.message || 'Failed to delete items');
    } finally {
      setDelPending(false);
    }
  };

  const handleQuery = async () => {
    try {
      setOpError(null);
      setQueryPending(true);
      if (!qText.trim()) throw new Error('Enter a query text');
      if (!data?.id) throw new Error('Collection ID is not loaded yet');
      const res = await queryItemsByText(data.id, { query: qText, nResults: qResults });
      setQueryResult(res);
    } catch (e: any) {
      setOpError(e?.message || 'Failed to query items');
    } finally {
      setQueryPending(false);
    }
  };

  return (
    <div className="space-y-4">
      <nav className="text-sm" aria-label="Breadcrumb">
        <ol className="mb-2 flex items-center gap-2 text-slate-500">
          <li><RouterLink to="/admin/chroma" className="hover:text-slate-700">Collections</RouterLink></li>
          <li aria-hidden className="text-slate-300">/</li>
          <li className="text-slate-700">{routeName}</li>
        </ol>
      </nav>

      {isLoading && <div className="text-sm text-slate-500">Loading...</div>}
      {isError && <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{(error as any)?.message || 'Failed to load collection'}{(error as any)?.message?.includes('403') ? ' (Access denied)':''}</div>}

      {data && (
        <div className="mx-auto max-w-6xl space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
            <div className="flex flex-wrap gap-2">
              <button type="button" className={`rounded-md px-3 py-2 text-sm ${tab==='info'?'bg-slate-800 text-white':'text-slate-700 hover:bg-slate-50'}`} onClick={() => setTab('info')}>Info</button>
              <button type="button" className={`rounded-md px-3 py-2 text-sm ${tab==='add'?'bg-slate-800 text-white':'text-slate-700 hover:bg-slate-50'}`} onClick={() => setTab('add')}>Add Item</button>
              <button type="button" className={`rounded-md px-3 py-2 text-sm ${tab==='delete'?'bg-slate-800 text-white':'text-slate-700 hover:bg-slate-50'}`} onClick={() => setTab('delete')}>Delete Items</button>
              <button type="button" className={`rounded-md px-3 py-2 text-sm ${tab==='query'?'bg-slate-800 text-white':'text-slate-700 hover:bg-slate-50'}`} onClick={() => setTab('query')}>Query</button>
            </div>
          </div>

          {tab==='info' && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 text-sm font-medium text-slate-700">Collection Info</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">ID</label>
                  <input readOnly className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm" value={data.id} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">Name</label>
                  <input readOnly className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm" value={data.name} />
                </div>
                {typeof (data as any).size === 'number' && (
                  <div>
                    <label className="mb-1 block text-xs text-slate-600">Entries (count)</label>
                    <input readOnly className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm" value={(data as any).size} />
                  </div>
                )}
                {data.metadata && (
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs text-slate-600">Metadata</label>
                    <textarea readOnly className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm" rows={4} value={JSON.stringify(data.metadata, null, 2)} />
                  </div>
                )}
              </div>
            </div>
          )}

          {tab==='add' && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 text-sm font-medium text-slate-700">Add Item</div>
              {opError && <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{opError}</div>}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">Name</label>
                  <input className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" value={itemName} onChange={e => setItemName(e.target.value)} required />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">Document (optional)</label>
                  <textarea className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" rows={6} placeholder='Paste document text here' value={document} onChange={e => setDocument(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={handleAdd} disabled={addPending}>Add</button>
                </div>
                {createdId && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">Item created. Generated ID: {createdId}</div>}
              </div>
            </div>
          )}

          {tab==='delete' && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 text-sm font-medium text-slate-700">Delete Items</div>
              {opError && <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{opError}</div>}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">IDs (one per line)</label>
                  <textarea className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" rows={6} placeholder={'id-1\nid-2\nid-3'} value={idsText} onChange={e => setIdsText(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" className="rounded-md border border-rose-200 bg-rose-600 px-3 py-2 text-sm text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={handleDelete} disabled={delPending}>Delete by IDs</button>
                </div>
              </div>
            </div>
          )}

          {tab==='query' && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 text-sm font-medium text-slate-700">Query</div>
              {opError && <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{opError}</div>}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">Query Text</label>
                  <textarea className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" rows={4} placeholder='Search phrase or question...' value={qText} onChange={e => setQText(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">n_results</label>
                  <input type="number" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" value={qResults} onChange={e => setQResults(parseInt(e.target.value || '2'))} />
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60" onClick={handleQuery} disabled={queryPending}>Run Query</button>
                </div>
                {queryResult && (
                  <div>
                    <div className="text-xs font-medium text-slate-600">Result</div>
                    <pre className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">{JSON.stringify(queryResult, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
